import { type AnchorHTMLAttributes, forwardRef } from "react";
import { MessageCircle } from "lucide-react";
import { buildWppUrl } from "@/lib/whatsapp";
import { track } from "@/lib/tracking";

interface CTAButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  eventName: string;
  variant?: "primary" | "light" | "outlineDark";
  size?: "md" | "lg" | "xl";
  fullWidth?: boolean;
}

/**
 * CTA principal — Todos agora abrem o WhatsApp direto.
 */
export const CTAButton = forwardRef<HTMLAnchorElement, CTAButtonProps>(
  (
    { eventName, className = "", onClick, variant = "primary", size = "lg", fullWidth = false, ...rest },
    ref,
  ) => {
    let base = "";

    if (variant === "primary") {
      base =
        "bg-gradient-to-b from-[#1A8B60] to-[#125C3F] hover:from-[#1F9C6C] hover:to-[#0F5137] text-white shadow-[0_16px_40px_-14px_rgba(23,114,79,0.65),inset_0_1px_0_rgba(255,255,255,0.14)]";
    } else if (variant === "light") {
      base =
        "bg-white text-[#0F2A47] hover:bg-[#F1EEE7] shadow-[0_14px_36px_-12px_rgba(11,18,32,0.4)]";
    } else {
      base =
        "bg-transparent text-white border border-white/25 hover:bg-white/10 shadow-none";
    }

    const height =
      size === "xl"
        ? "h-[58px] md:h-[66px]"
        : size === "lg"
        ? "h-[54px] md:h-[60px]"
        : "h-[52px] md:h-14";
    const paddingX = size === "xl" ? "px-5 md:px-9" : "px-4 md:px-7";
    const textSize =
      size === "xl" ? "text-[15px] md:text-[17px]" : "text-[14.5px] md:text-[16.5px]";
    const width = fullWidth ? "w-full" : "w-full sm:w-auto";

    return (
      <a
        ref={ref}
        href={buildWppUrl()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          track({ event: "click_whatsapp", source: eventName });
          onClick?.(e);
        }}
        className={
          "group relative inline-flex items-center justify-center gap-3 overflow-hidden whitespace-nowrap " +
          `${height} ${width} ${paddingX} ` +
          `rounded-full font-semibold ${textSize} tracking-[0.005em] ` +
          "transition-all duration-300 ease-out active:scale-[0.98] cursor-pointer " +
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#17724F] " +
          base + " " + className
        }
        {...rest}
      >
        {variant === "primary" && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-px rounded-full opacity-60"
            style={{
              background:
                "radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,0.35), transparent 60%)",
            }}
          />
        )}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 h-full w-2/3 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent motion-safe:animate-[shimmer-move_3.6s_ease-in-out_infinite] will-change-transform"
        />
        <span className="relative sm:hidden">Falar no WhatsApp</span>
        <span className="relative hidden sm:inline">Falar no WhatsApp agora</span>
        <span
          aria-hidden="true"
          className="relative grid h-7 w-7 md:h-8 md:w-8 place-items-center rounded-full bg-white/12 transition-all duration-300 group-hover:bg-white/22 group-hover:translate-x-0.5"
        >
          <MessageCircle size={14} strokeWidth={2.4} className="md:hidden" />
          <MessageCircle size={16} strokeWidth={2.4} className="hidden md:block" />
        </span>
      </a>
    );
  },
);

CTAButton.displayName = "CTAButton";
